import type { PlanListItem } from '@tabel/shared/schemas/rules-plan';
import { useQuery } from '@tanstack/react-query';
import { Bot, ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button, Card, EmptyState, Skeleton } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { reportClientError } from '../../lib/errors';
import { ruDate, ruDateTime } from '../../lib/format';
import { useRole } from '../../lib/role';
import { LoadError, shouldReport } from './LoadError';
import { PLAN_LABEL, PLAN_T, asSentence, sayPlan } from './plan-texts';
import { LABEL } from './texts';

/*
 * Вид «Планы агентов» (005, FR-065; contracts/http-api.md, `GET /api/rules/plans`): планы изменения, которые
 * заморозили ИИ-агенты, новые сверху. Без ссылки агента план иначе не найти (приёмка T153, 18.10.2026). Строка: номер,
 * состояние, дата начала новых правил, клиент агента и время заморозки, срок или текст состояния, изменения словами,
 * «Открыть план» — та же панель плана, что по ссылке. Видят администратор правил и утверждающий (права плана по
 * ссылке). Запрос — общий с полосой черновика (`usePlans`).
 */

const TONE: Readonly<Record<PlanListItem['state'], string>> = {
  active: 'bg-accent-soft text-accent ring-accent/20',
  stale: 'bg-warn-soft text-warn ring-warn/20',
  expired: 'bg-subtle text-ink-2 ring-line',
  published: 'bg-ok-soft text-ok ring-ok/20',
};

const STATE_LABEL: Readonly<Record<PlanListItem['state'], string>> = {
  active: PLAN_LABEL.stateActive,
  stale: PLAN_LABEL.stateStale,
  expired: PLAN_LABEL.stateExpired,
  published: PLAN_LABEL.statePublished,
};

/** Планы объекта: вкладка «Планы агентов» и строка о плане в полосе черновика. */
export function usePlans(enabled: boolean) {
  const { role } = useRole();
  return useQuery({
    queryKey: rulesKeys.plans(role),
    queryFn: async () => {
      try {
        return await api.rulesPlans();
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'load plans', e);
        throw e;
      }
    },
    enabled,
  });
}

/** Действующий план открытого черновика: последний (список — новые сверху). */
export function draftPlanOf(plans: readonly PlanListItem[] | undefined): PlanListItem | undefined {
  return plans?.find((p) => p.state === 'active' && p.currentDraft);
}

function Plan({
  plan,
  tzOffset,
  onOpen,
}: {
  plan: PlanListItem;
  tzOffset: string;
  onOpen: (planId: number) => void;
}) {
  const state =
    plan.state === 'active'
      ? sayPlan.active(ruDateTime(plan.expiresAt, tzOffset))
      : asSentence(plan.stateText ?? PLAN_T.staleFallback);
  return (
    <li className="space-y-2 px-5 py-4 max-sm:px-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <h3 className="text-[14px] font-semibold text-ink">{sayPlan.title(plan.planId)}</h3>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-medium ring-1 ring-inset ${TONE[plan.state]}`}
        >
          {STATE_LABEL[plan.state]}
        </span>
      </div>
      <p className="text-[13.5px] text-ink">{sayPlan.validFrom(ruDate(plan.validFrom))}</p>
      {plan.changes.length > 0 && (
        <ul aria-label={PLAN_LABEL.changes} className="list-disc space-y-0.5 pl-5 text-[13px] text-ink-2">
          {plan.changes.map((c) => (
            <li key={c} className="break-words">
              {c}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[12.5px] text-ink-2">
        {sayPlan.frozen(plan.preparedBy.client, ruDateTime(plan.preparedBy.frozenAt, tzOffset))} {state}
      </p>
      <div className="pt-1 max-sm:[&>button]:w-full">
        <Button icon={ExternalLink} onClick={() => onOpen(plan.planId)}>
          {PLAN_LABEL.open}
        </Button>
      </div>
    </li>
  );
}

export function PlansView({ tzOffset, onOpen }: { tzOffset: string; onOpen: (planId: number) => void }) {
  const q = usePlans(true);
  const list = q.data?.plans ?? [];

  let body: ReactNode;
  if (q.isLoading)
    body = (
      <div aria-busy="true" aria-label={LABEL.loading} className="space-y-3">
        <Skeleton className="h-24 w-full rounded-card" />
        <Skeleton className="h-24 w-full rounded-card" />
      </div>
    );
  else if (q.error)
    body = <LoadError error={q.error} title={PLAN_LABEL.listError} onRetry={() => void q.refetch()} />;
  else if (list.length === 0)
    body = (
      <Card>
        <EmptyState icon={Bot} title={PLAN_T.listEmpty} />
      </Card>
    );
  else
    body = (
      <Card>
        <ul aria-label={PLAN_LABEL.tab} className="divide-y divide-line">
          {list.map((p) => (
            <Plan key={p.planId} plan={p} tzOffset={tzOffset} onOpen={onOpen} />
          ))}
        </ul>
      </Card>
    );

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-ink-2">{PLAN_T.listIntro}</p>
      {body}
    </div>
  );
}
